"use client";

import React, { memo, useDeferredValue, useMemo } from "react";
import { Text, plainText } from "../../atoms/text/text";
import { GenrePackList, type GenrePackListProps } from "./genre-pack-list";
import { browsableGenrePacks, searchedGenrePacks } from "./genre-pack-list.shared";
import { useLineupMarketRows } from "./lineup-market.client";
import { withMarketPackChannels } from "./lineup-market.shared";
import { useLineupSearchSelection } from "./lineup-search.client";
import styles from "./genre-pack-list.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * The Genre Packs view under an active search (GP10, GP15, GP1's divider rule) — a thin wrapper
 * that decides which packs, and which of their rows, reach `genre-pack-list.tsx`.
 */

const MemoGenrePackList = memo(GenrePackList);

export interface SearchableGenrePackListProps extends GenrePackListProps {
  /** GP10 — the message shown when no pack matches the search. A third state, not a third `status`. */
  search_empty_message_content?: string;
}

function SearchableGenrePackListImpl({
  packs: serverPacks,
  search_empty_message_content: searchEmptyMessageContent,
  status = "ready",
  className,
  ...rest
}: SearchableGenrePackListProps) {
  const { channelIds, subscriptionIds, isActive } = useDeferredValue(useLineupSearchSelection());
  const { packChannels } = useLineupMarketRows();
  // A Genre pack follows the market: this ZIP's channels filed under its code join it.
  const packs = useMemo(
    () => withMarketPackChannels(serverPacks, packChannels),
    [serverPacks, packChannels]
  );

  /* Two rules, two branches. GP15's browse half runs whenever no search is active, and is not a
     weaker form of GP10 — see `genre-pack-list.shared.ts`. */
  const shown = useMemo(
    () => (isActive ?
      searchedGenrePacks(packs, {
        channelIds,
        subscriptionIds
      }) :
      browsableGenrePacks(packs)),
    [channelIds, isActive, packs, subscriptionIds]
  );

  /* GP11 wins over both filters: a view that could not load has no set of
     packs to narrow, and `GenrePackList` ignores `packs` in that state. */
  if (status === "error") {
    return <MemoGenrePackList {...rest} className={className} packs={packs} status={status} />;
  }

  if (isActive && shown.length === 0) {
    // GP10's third clause. Same block as the Signature grid's own no-match
    // state (`signature-grid.tsx:231-239`), so the two views read alike.
    const message = plainText((searchEmptyMessageContent ?? "").trim());
    return (
      <div className={[styles.list, className].filter(Boolean).join(" ")}>
        <Text as="p" className={styles.empty} variant="body-2">
          {message}
        </Text>
      </div>
    );
  }

  return <MemoGenrePackList {...rest} className={className} packs={shown} status={status} />;
}
export const SearchableGenrePackList = withSafeRender(SearchableGenrePackListImpl, "SearchableGenrePackList");

export default SearchableGenrePackList;
